import type { PackEntry } from 'types/shared';
import type { CreativeDnDMovePayload } from '../../types/hypothesis';

export const applyPackCreativesMove = (
  packs: PackEntry[],
  { creativeId, from, to }: CreativeDnDMovePayload,
): PackEntry[] => {
  const next = packs.map((p) => ({ ...p, creatives: [...p.creatives] }));

  if (from.kind === 'unpacked' && to.kind === 'unpacked') {
    return next;
  }

  if (from.kind === 'pack' && to.kind === 'pack' && from.packIndex === to.packIndex) {
    const p = next[from.packIndex];
    if (!p) return next;
    const arr = [...p.creatives];
    const fromIdx =
      arr[from.index] === creativeId ? from.index : arr.indexOf(creativeId);
    if (fromIdx < 0) return next;
    const [removed] = arr.splice(fromIdx, 1);
    const insertAt = Math.min(Math.max(0, to.index), arr.length);
    arr.splice(insertAt, 0, removed);
    p.creatives = arr;
    return next;
  }

  if (from.kind === 'pack') {
    const p = next[from.packIndex];
    if (p) {
      if (p.creatives[from.index] === creativeId) {
        p.creatives.splice(from.index, 1);
      } else {
        const idx = p.creatives.indexOf(creativeId);
        if (idx >= 0) p.creatives.splice(idx, 1);
      }
    }
  } else {
    for (const p of next) {
      p.creatives = p.creatives.filter((id) => id !== creativeId);
    }
  }

  if (to.kind === 'pack') {
    const p = next[to.packIndex];
    if (p) {
      p.creatives = p.creatives.filter((id) => id !== creativeId);
      const insertAt = Math.min(Math.max(0, to.index), p.creatives.length);
      p.creatives.splice(insertAt, 0, creativeId);
    }
  }

  return next;
};

export interface PruneEmptyPacksResult {
  packs: PackEntry[];
  removedIndices: number[];
}

/** Drops packs with no creatives (e.g. after dragging the last creative out). */
export const pruneEmptyPacks = (packs: PackEntry[]): PruneEmptyPacksResult => {
  const removedIndices: number[] = [];
  const nextPacks = packs.filter((pack, index) => {
    if (pack.creatives.length > 0) return true;
    removedIndices.push(index);
    return false;
  });
  return { packs: nextPacks, removedIndices };
};

export const adjustActivePackIndexAfterRemoval = (
  activeIndex: number | null,
  removedIndices: number[],
): number | null => {
  if (activeIndex === null || removedIndices.length === 0) return activeIndex;
  if (removedIndices.includes(activeIndex)) return null;
  const shift = removedIndices.filter((index) => index < activeIndex).length;
  return activeIndex - shift;
};
